// How a checkout preview is presented, and what its button does.
//
// GET /billing/checkout/preview always answers with the same eleven fields;
// `type` says which case it is. The API supplies every number and sentence,
// but none of this wording or styling, and three screens render the same
// quote - the change-plan modal, the billing page and the public checkout -
// so it lives in one place rather than three.

/** What the confirm button should call when it is enabled. */
export const ACTIONS = {
  CHECKOUT: "checkout",
  RETRY: "retry",
  CANCEL: "cancel",
  NONE: "none",
};

export const TYPE_META = {
  // Never paid before.
  new_subscription: {
    title: "Subscription summary",
    action: ACTIONS.CHECKOUT,
  },
  // Cancelled, the period has run out and the account is paused. Any plan.
  resubscribe: {
    title: "Start again",
    badge: "Restart",
    tone: "info",
    action: ACTIONS.CHECKOUT,
  },
  // A higher plan, or the same plan monthly -> yearly.
  upgrade: {
    title: "Upgrade summary",
    badge: "Upgrade",
    tone: "success",
    action: ACTIONS.CHECKOUT,
  },
  // Already on it.
  no_change: {
    title: "Plan summary",
    badge: "Current plan",
    tone: "info",
    action: ACTIONS.NONE,
  },
  // A lower plan, or yearly -> monthly, while the paid period is still
  // running. The way through is to turn off auto-renewal and come back.
  downgrade_locked: {
    title: "Downgrade",
    badge: "Not yet available",
    tone: "warn",
    action: ACTIONS.CANCEL,
  },
  // The last renewal failed. Nothing can be bought until it is paid.
  payment_failed: {
    title: "Payment failed",
    badge: "Action needed",
    tone: "danger",
    action: ACTIONS.RETRY,
  },
  // Cancelled, but the paid period has not ended yet.
  blocked: {
    title: "Not available yet",
    badge: "Cancelled",
    tone: "warn",
    action: ACTIONS.NONE,
  },
};

/** The type's metadata, with sane defaults for a type added server-side later. */
export const metaFor = (preview) =>
  TYPE_META[preview?.type] || { title: "Order summary", action: ACTIONS.CHECKOUT };

/**
 * Whether the quote is something to resolve rather than something to confirm.
 * `action.disabled` is the server's own answer and is trusted first; the type
 * is only a fallback for a response that leaves the flag out.
 */
export const isBlockedPreview = (preview) => {
  const action = preview?.action || preview?.cta;

  if (action && typeof action.disabled === "boolean") return action.disabled;

  return ["no_change", "downgrade_locked", "blocked"].includes(preview?.type);
};

/**
 * The plan cannot be bought until the account has few enough active
 * assistants for it. Only `new_subscription` and `resubscribe` carry this.
 */
export const needsFewerAssistants = (preview) =>
  preview?.requirementsMet === false && Boolean(preview?.assistants);
